import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import ConfirmModal from "../../components/common/ConfirmModal";
import {
  getAllPlans,
  deletePlan,
  toggleVisible,
  getPlanPlatformTypes,
  type SubscriptionPlan,
} from "../../services/subscriptionService";
import { Building2, CreditCard, Pencil, Stethoscope, Trash2, Users } from "lucide-react";

type PlatformTab = "hospital" | "doctor" | "public";

const PLATFORM_LABELS: Record<string, string> = {
  hospital: "Hospital",
  doctor: "Doctor",
  public: "Public User",
};

const AdminSubscriptionPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<PlatformTab>("hospital");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState<SubscriptionPlan | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const data = await getAllPlans();
      setPlans(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load subscription plans");
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const visiblePlans = plans.filter((plan) =>
    getPlanPlatformTypes(plan).includes(activeTab)
  );

  const handleDeleteClick = (plan: SubscriptionPlan) => {
    setDeletingPlan(plan);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingPlan) return;
    setDeleting(true);
    try {
      await deletePlan(deletingPlan.id);
      toast.success("Plan deleted");
      setShowDeleteModal(false);
      setDeletingPlan(null);
      loadPlans();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleVisible = async (plan: SubscriptionPlan) => {
    setTogglingId(plan.id);
    try {
      await toggleVisible(plan.id);
      toast.success(plan.isVisible ? "Plan hidden" : "Plan visible");
      loadPlans();
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Toggle failed";
      toast.error(msg);
    } finally {
      setTogglingId(null);
    }
  };

  const formatLimit = (value?: number | null) => {
    if (value === null || value === undefined) return "Unlimited";
    return value;
  };

  const tabs: { key: PlatformTab; label: string; icon: React.ReactNode }[] = [
    { key: "hospital", label: "Hospital", icon: <Building2 size={16} /> },
    { key: "doctor", label: "Doctor", icon: <Stethoscope size={16} /> },
    { key: "public", label: "Public User", icon: <Users size={16} /> },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? "bg-primary-600 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => navigate(`/super-admin/subscription/new?platform=${activeTab}`)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
        >
          <CreditCard size={18} />
          Add Subscription
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4" />
              <p className="text-gray-500 animate-pulse">Loading plans...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Platform</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Case Limit</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User Limit</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Visible</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {visiblePlans.map((plan) => (
                    <tr key={plan.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-gray-900">{plan.planName}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1.5">
                          {getPlanPlatformTypes(plan).map((platform) => (
                            <span
                              key={platform}
                              className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                            >
                              {PLATFORM_LABELS[platform] || platform}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {plan.price} {plan.currency}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {plan.durationValue} {plan.durationType}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatLimit(plan.caseLimit)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatLimit(plan.userLimit)}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          onClick={() => handleToggleVisible(plan)}
                          disabled={togglingId === plan.id}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                            plan.isVisible ? "bg-primary-600" : "bg-gray-200"
                          } ${togglingId === plan.id ? "opacity-50" : ""}`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                              plan.isVisible ? "translate-x-5" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/super-admin/subscription/${plan.id}/edit`)}
                            className="p-2 rounded-lg text-primary-500 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(plan)}
                            className="p-2 rounded-lg text-danger-500 hover:bg-danger-50 hover:text-danger-700 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visiblePlans.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                        No {activeTab === "public" ? "public user" : activeTab} subscription plans yet. Add one to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete Plan"
        message={deletingPlan ? `Are you sure you want to delete "${deletingPlan.planName}"?` : ""}
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeletingPlan(null);
        }}
      />
    </div>
  );
};

export default AdminSubscriptionPage;